import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Paper, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Skeleton, 
    Button, 
    Chip, 
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CloseIcon from '@mui/icons-material/Close';
import { FinanceService } from '../../../api/services/financeService';
import LogExpenseModal from './LogExpenseModal';
import { formatCurrency } from '../../../utils/currency';
import { extractTotalCount } from '../../../components/common/tableStyles';

// Inline formatters
const formatDate = (date) => new Date(date).toLocaleDateString();

export default function FinanceExpensesTab() {
    const [expenses, setExpenses] = useState([]);
    const [totalExpenses, setTotalExpenses] = useState(0);
    const [loading, setLoading] = useState(true);
    const [expenseModalOpen, setExpenseModalOpen] = useState(false);
    const [receiptPopup, setReceiptPopup] = useState(null);

    // Client-side pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const fetchExpenses = async () => {
        setLoading(true);
        try {
            const data = await FinanceService.getExpenses({ page, limit: rowsPerPage });
            if (data?.data && Array.isArray(data.data)) {
                setExpenses(data.data);
                setTotalExpenses(extractTotalCount(data, data.data));
            } else if (Array.isArray(data)) {
                setExpenses(data);
                setTotalExpenses(extractTotalCount(data, data));
            } else {
                setExpenses([]);
                setTotalExpenses(0);
            }
        } catch (error) {
            console.error('Failed to fetch expense ledger:', error);
            setExpenses([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchExpenses();
    }, [page, rowsPerPage]);

    return (
        <Box sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccountBalanceIcon color="primary" sx={{ fontSize: 28 }} />
                    <Typography variant="h6" fontWeight="bold">
                        Operational Expense Ledger
                    </Typography>
                </Box>
                <Button 
                    variant="contained" 
                    color="primary"
                    startIcon={<RequestQuoteIcon />}
                    onClick={() => setExpenseModalOpen(true)}
                    sx={{ borderRadius: 2 }}
                >
                    Log Expense
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Voucher / Reference ID</TableCell>
                            <TableCell>Posting Date</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Description / Narrative</TableCell>
                            <TableCell>Payment Method</TableCell>
                            <TableCell>Receipt</TableCell>
                            <TableCell align="right">Amount</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            Array.from(new Array(5)).map((_, idx) => (
                                <TableRow key={idx}>
                                    <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                                    <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                                    <TableCell><Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} /></TableCell>
                                    <TableCell><Skeleton variant="text" width="90%" /></TableCell>
                                    <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                                    <TableCell><Skeleton variant="text" width="50%" /></TableCell>
                                    <TableCell align="right"><Skeleton variant="text" width="40%" sx={{ ml: 'auto' }} /></TableCell>
                                </TableRow>
                            ))
                        ) : expenses.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                                    <Typography color="text.secondary">No operational expenses have been logged yet.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            expenses.map((expense) => (
                                <TableRow key={expense.id || expense._id} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
                                            {expense.referenceNumber || expense.id || expense._id}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {formatDate(expense.date || expense.createdAt)}
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={expense.category} 
                                            size="small" 
                                            color="default" 
                                            sx={{ fontWeight: 'medium' }} 
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {expense.description}
                                    </TableCell>
                                    <TableCell>
                                        {expense.paymentMethod}
                                    </TableCell>
                                    <TableCell>
                                        {expense.receiptUrl ? (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="primary"
                                                onClick={() => setReceiptPopup(expense.receiptUrl)}
                                                startIcon={<ReceiptLongIcon sx={{ fontSize: 14 }} />}
                                                sx={{ textTransform: 'none', py: 0.25, px: 1, fontSize: '0.75rem', borderRadius: 1.5 }}
                                            >
                                                Receipt
                                            </Button>
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">—</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography fontWeight="bold" color="error.main">
                                            {formatCurrency(expense.amount)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                
                <TablePagination
                    component="div"
                    count={expenses.length === 0 ? 0 : totalExpenses}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[20, 50, 100]}
                />
            </TableContainer>

            <LogExpenseModal
                open={expenseModalOpen}
                onClose={() => setExpenseModalOpen(false)}
                onSuccess={() => fetchExpenses()}
            />

            {/* Receipt Preview Popup Dialog */}
            <Dialog 
                open={!!receiptPopup} 
                onClose={() => setReceiptPopup(null)} 
                maxWidth="md" 
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    borderBottom: '1px solid #eee',
                    bgcolor: '#fbfbfb',
                    py: 1.5,
                    px: 3
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ReceiptLongIcon color="primary" />
                        <Typography variant="subtitle1" fontWeight="bold">
                            Expense Receipt
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {receiptPopup && (
                            <Button
                                size="small"
                                variant="text"
                                color="primary"
                                href={receiptPopup}
                                target="_blank"
                                rel="noopener noreferrer"
                                startIcon={<OpenInNewIcon fontSize="small" />}
                                sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                            >
                                Open in new tab
                            </Button>
                        )}
                        <IconButton onClick={() => setReceiptPopup(null)} size="small" sx={{ color: 'grey.500' }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2, minHeight: 300, bgcolor: '#fafafa' }}>
                    {receiptPopup && (
                        receiptPopup.toLowerCase().includes('.pdf') ? (
                            <Box 
                                component="iframe" 
                                src={receiptPopup} 
                                title="Expense Receipt Document" 
                                sx={{ width: '100%', height: '75vh', border: 'none', borderRadius: 2 }} 
                            />
                        ) : (
                            <Box 
                                component="img" 
                                src={receiptPopup} 
                                alt="Expense Receipt" 
                                sx={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }} 
                            />
                        )
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}
